import { makeAutoObservable } from 'mobx';

import { foodScoringService } from '../services/foodScoringService';
import { TAB_VALUES, TabValue } from '../constants/TAB_VALUES';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { getFoodsWithRoundedNutrients } from '../utils';
import {
  FoodScorePageResponse,
  FoodScorePayload,
  FoodScoreType,
  FoodWithScore,
} from '../interfaces/FoodScorePageResponse';

const { WORKING_TABLE_TAB } = TAB_VALUES;

class FoodScoreStore {
  activeTab: TabValue = WORKING_TABLE_TAB;

  workingTableData: FoodScorePageResponse | null = null;
  workingTableTotal = 0;

  finalTableData: FoodScorePageResponse | null = null;
  finalTableTotal = 0;

  foodScoreTypes: FoodScoreType[] = [];
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setDefaultValues() {
    this.activeTab = WORKING_TABLE_TAB;
    this.workingTableData = null;
    this.workingTableTotal = 0;
    this.finalTableData = null;
    this.finalTableTotal = 0;
    this.foodScoreTypes = [];
  }

  setActiveTab(activeTab: TabValue) {
    this.activeTab = activeTab;
  }

  async getWorkingTableData(page?: number) {
    const workingTableData = await foodScoringService.getWorkingTable(page ?? 0);
    this.setWorkingTableData(workingTableData);
    this.setWorkingTableTotal(workingTableData.totalElements);
  }

  setWorkingTableData(data: FoodScorePageResponse) {
    const workingTableData = { ...data };
    if (data.content) {
      workingTableData.content = getFoodsWithRoundedNutrients(data.content);
    }
    this.workingTableData = workingTableData;
  }

  setWorkingTableTotal(workingTableTotal: number) {
    this.workingTableTotal = workingTableTotal;
  }

  async getFinalTableData(page?: number) {
    const finalTableData = await foodScoringService.getFinalTable(page ?? 0);
    this.setFinalTableData(finalTableData);
    this.setFinalTableTotal(finalTableData.totalElements);
  }

  setFinalTableData(data: FoodScorePageResponse) {
    const finalTableData = { ...data };
    if (data.content) {
      finalTableData.content = getFoodsWithRoundedNutrients(data.content);
    }
    this.finalTableData = finalTableData;
  }

  setFinalTableTotal(finalTableTotal: number) {
    this.finalTableTotal = finalTableTotal;
  }

  async getFoodScoreTypes() {
    const foodScoreTypes = await foodScoringService.getFoodScoreTypes();
    this.setFoodScoreTypes(foodScoreTypes);
  }

  setFoodScoreTypes(foodScoreTypes: FoodScoreType[]) {
    this.foodScoreTypes = foodScoreTypes;
  }

  async deleteFood(foodId: number) {
    await foodScoringService.deleteFood(foodId);

    this.deleteFoodFromTableData(foodId);
  }

  async saveFoodScore(foodId: number, scoreResult: FoodScorePayload[]) {
    await foodScoringService.saveFoodScoring(foodId, scoreResult);

    this.deleteFoodFromTableData(foodId);
  }

  deleteFoodFromTableData(foodId: number) {
    const { tableData, setTableData } = this.getActiveTableDataConfig();

    if (!tableData) {
      return;
    }

    const newFoods = tableData.content.filter(
      (food: FoodWithScore) => food.foodId !== foodId,
    );

    setTableData({ ...tableData, content: newFoods });
  }

  getActiveTableDataConfig() {
    if (this.activeTab === WORKING_TABLE_TAB) {
      return { tableData: this.workingTableData, setTableData: this.setWorkingTableData };
    } else {
      return { tableData: this.finalTableData, setTableData: this.setFinalTableData };
    }
  }
}

const foodScoreStore = new FoodScoreStore();

resetAllStore.addResetMethod(foodScoreStore.setDefaultValues);

export { foodScoreStore, FoodScoreStore };
